import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useParams, useHistory, Link } from "react-router-dom";
import apiService from "services/api";
import { useNotificationContext } from "contexts";
import { CustomModal, CustomTable, FormCheck, FormSelect } from "components";
import ReceiptModal from "./ReceiptModal";
import { useFieldArray, useForm } from "react-hook-form";
import {
    initialState,
    emptyChildForm,
    columns,
    phoneNumberCountryCodes,
    addPointsRecordsColumns,
} from "./constants";
import { FormEntry } from "components";
import { FiEdit, FiSearch } from "react-icons/fi";
import { Button } from "reactstrap";

const EditParentPage = () => {
    const { parent_id } = useParams();
    const history = useHistory();
    const [defaultUser, setDefaultUser] = useState({});
    const [displayReceiptModal, setDisplayReceiptModal] = useState(false);
    const [receiptIdToShow, setReceiptIdToShow] = useState("");
    const { createNotification } = useNotificationContext();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: initialState,
    });

    const { data, isLoading } = useQuery(
        ["parent", parent_id],
        () => apiService.parent.get(parent_id),
        {
            refetchOnwindowFocus: false,
            onSuccess: (data) => {
                reset(data);
                setDefaultUser(data);
            },
        }
    );

    const submitEdit = (data) => {
        apiService.parent
            .update(parent_id, data)
            .then(() => {
                createNotification({
                    type: "success",
                    message: "Successfully updated member",
                });
                history.push("/parent");
            })
            .catch((_) =>
                createNotification({
                    type: "danger",
                    message: "Failed to edit member",
                })
            );
    };

    const resetFields = () => {
        reset(defaultUser);
    };

    if (isLoading) {
        return <div className="content">Loading...</div>;
    }

    const showReceiptModal = (row) => {
        setReceiptIdToShow(row.id);
        setDisplayReceiptModal(true);
    };

    const closeReceiptModal = () => {
        setDisplayReceiptModal(false);
    };

    const actions = (row) => (
        <div className="actions-right">
            <Button
                onClick={() => showReceiptModal(row)}
                color="warning"
                size="sm"
                className="btn-icon btn-link edit"
            >
                <FiSearch />
            </Button>
            {/* <Button
                onClick={() =>
                    history.push(`/coffee/add-points/edit/${row.id}`)
                }
                color="warning"
                size="sm"
                className="btn-icon btn-link edit"
            >
                <FiEdit />
            </Button> */}
        </div>
    );

    return (
        <>
            <CustomModal
                show={displayReceiptModal}
                hideModal={closeReceiptModal}
            >
                <ReceiptModal receiptIdToShow={receiptIdToShow} />
            </CustomModal>
            <div className="content">
                <form onSubmit={handleSubmit(submitEdit)} id="parentForm">
                    <div className="text-center mb-4 font-weight-bold text-white">
                        Edit Member
                    </div>
                    <div className="card card-body">
                        <div className="d-flex justify-content-between mb-4">
                            <Link to="/parent">
                                <button
                                    className="btn btn-sm btn-info"
                                    type="button"
                                >
                                    &lt; Back
                                </button>
                            </Link>
                            <div>
                                <button
                                    className="btn btn-success btn-sm mx-1"
                                    type="submit"
                                    form="parentForm"
                                >
                                    Submit
                                </button>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    type="button"
                                    onClick={resetFields}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                        <FormCheck
                            register={register}
                            name="active"
                            formLabel="Is Active?"
                        />
                        <div className="input-group mb-3">
                            <div className="input-group-prepend">
                                <span className="input-group-text">Name</span>
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                name="firstName"
                                {...register("firstName", { required: true })}
                                placeholder="First Name"
                            />
                            <input
                                type="text"
                                className="form-control"
                                name="lastName"
                                placeholder="Last Name"
                                {...register("lastName", { required: true })}
                            />
                            {errors.firstName && (
                                <div className=" text-center">
                                    <small className="text-danger">
                                        {errors.firstName?.message}
                                    </small>
                                </div>
                            )}
                            {errors.lastName && (
                                <div className=" text-center">
                                    <small className="text-danger">
                                        {errors.lastName?.message}
                                    </small>
                                </div>
                            )}
                        </div>
                        <FormEntry
                            formLabel="Email"
                            name="email"
                            placeholder="Member's email"
                            type="email"
                            error={errors.email}
                            register={register}
                        />
                        <FormEntry
                            formLabel="Password"
                            name="password"
                            placeholder="Member's Password"
                            type="password"
                            error={errors.password}
                            register={register}
                        />
                        <div className="input-group mb-3">
                            <div className="input-group-prepend">
                                <span className="input-group-text">
                                    Phone Number
                                </span>
                            </div>
                            <select
                                className="form-control"
                                {...register("phoneNumberCountryCode")}
                                placeholder="Country Code"
                            >
                                {phoneNumberCountryCodes.map(
                                    (phoneNumberCountryCode, index) => (
                                        <option
                                            value={phoneNumberCountryCode.code}
                                            key={index}
                                        >{`${phoneNumberCountryCode.country} (${phoneNumberCountryCode.code})`}</option>
                                    )
                                )}
                            </select>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Parent's Phone Number"
                                name="phoneNumber"
                                {...register("phoneNumber", {
                                    maxLength: 8,
                                    minLength: 8,
                                    required: true,
                                })}
                            />
                            {errors.phoneNumber && (
                                <div className=" text-center">
                                    <small className="text-danger">
                                        {errors.phoneNumber?.message}
                                    </small>
                                </div>
                            )}
                        </div>
                        <div className="row mb-1">
                            <div className="col">
                                <FormSelect
                                    formLabel="Gender"
                                    register={register}
                                    name="gender"
                                    required={true}
                                    error={errors.gender}
                                    disabled={isLoading}
                                    options={[
                                        { value: "Male", label: "Male" },
                                        { value: "Female", label: "Female" },
                                        {
                                            value: "PNTS",
                                            label: "Prefer not to say",
                                        },
                                    ]}
                                />
                            </div>
                            <div className="col">
                                <FormEntry
                                    register={register}
                                    formLabel="Number of Children"
                                    name="numberOfChildren"
                                    type="number"
                                    validation={{
                                        minValue: (v) => v >= 0,
                                    }}
                                    transformations={{
                                        setValueAs: (v) => parseInt(v),
                                    }}
                                />
                            </div>
                        </div>

                        <FormEntry
                            register={register}
                            formLabel="Address"
                            name="address"
                            type="text"
                            placeholder="Member's Address"
                        />

                        <div className="input-group mb-3">
                            <div className="input-group-prepend">
                                <label
                                    className="input-group-text pr-2"
                                    htmlFor="membershipType"
                                >
                                    Membership Type
                                </label>
                            </div>
                            <select
                                className="form-control custom-select"
                                {...register("membershipType", {
                                    required: true,
                                })}
                            >
                                <option value="VIP">VIP</option>
                                <option value="Group">Group</option>
                                <option value="Coffee">Coffee</option>
                                <option value="Staff">Staff</option>
                                <option value="Guest">Guest</option>
                            </select>
                        </div>
                    </div>
                </form>

                {!isLoading && (
                    <div>
                        <CustomTable
                            data={data.transactions}
                            columns={columns}
                            defaultPageSize={10}
                            title={`${data.firstName} ${data.lastName}'s transactions`}
                            // csvName={`${data.firstName}_${data.lastName}_Transaction_Report`}
                        />
                        <CustomTable
                            data={data?.addPointRecords}
                            columns={addPointsRecordsColumns}
                            defaultPageSize={10}
                            title={`${data.firstName} ${data.lastName}'s add point record`}
                            actions={actions}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default EditParentPage;
